/**
 * Issue #6 — persisted "Logsheet Throughput window" preference.
 *
 * Mirrors `sidebar-state.ts`: every localStorage read/write is wrapped in
 * try/catch and the UI must render fine with no stored value (default 14).
 * The [7, 90] clamp mirrors the backend's silent server-side clamp on
 * `GET /api/dashboard/summary?throughputDays=N`.
 */

export const THROUGHPUT_DAYS_DEFAULT = 14;
export const THROUGHPUT_DAYS_MIN = 7;
export const THROUGHPUT_DAYS_MAX = 90;
export const THROUGHPUT_DAYS_PRESETS = [14, 30, 60, 90] as const;

const STORAGE_KEY = 'aryan-throughput-days';

/** Clamp an arbitrary number into the server-accepted [7, 90] window. */
export function clampThroughputDays(days: number): number {
  if (!Number.isFinite(days)) {
    return THROUGHPUT_DAYS_DEFAULT;
  }
  return Math.min(THROUGHPUT_DAYS_MAX, Math.max(THROUGHPUT_DAYS_MIN, Math.trunc(days)));
}

/**
 * Read the persisted throughput-window preference.
 * Returns the default (14) when no preference is set, the value isn't parsable,
 * or window/localStorage is unavailable.
 */
export function readThroughputDays(): number {
  if (typeof window === 'undefined' || typeof window.localStorage === 'undefined') {
    return THROUGHPUT_DAYS_DEFAULT;
  }
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (raw === null) {
      return THROUGHPUT_DAYS_DEFAULT;
    }
    const parsed = Number.parseInt(raw, 10);
    return Number.isNaN(parsed) ? THROUGHPUT_DAYS_DEFAULT : clampThroughputDays(parsed);
  } catch {
    // QuotaExceeded / disabled storage / etc. Fail safe to the default window.
    return THROUGHPUT_DAYS_DEFAULT;
  }
}

/** Persist the throughput-window preference (clamped). Silent on storage errors. */
export function writeThroughputDays(days: number): void {
  if (typeof window === 'undefined' || typeof window.localStorage === 'undefined') {
    return;
  }
  try {
    window.localStorage.setItem(STORAGE_KEY, String(clampThroughputDays(days)));
  } catch {
    // Silent: not worth crashing the UI over.
  }
}
